import { useMutation, useQuery } from '@tanstack/react-query';
import { Award, Clock, Download, ExternalLink, ShieldCheck, ShieldX } from 'lucide-react';
import { certificadoService } from '../../shared/services';
import type { CertificadoEmitido, StatusCertificado } from '../../shared/types';

const STATUS: Record<StatusCertificado, { label: string; className: string; Icon: typeof ShieldCheck }> = {
  valido: { label: 'Válido', className: 'bg-green-100 text-green-800', Icon: ShieldCheck },
  expirado: { label: 'Expirado', className: 'bg-amber-100 text-amber-800', Icon: Clock },
  revogado: { label: 'Revogado', className: 'bg-red-100 text-red-800', Icon: ShieldX },
};

function formatarCarga(minutos: number) {
  if (!minutos || minutos <= 0) return '—';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} min`;
  return resto === 0 ? `${horas}h` : `${horas}h ${resto}min`;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function CertificatesPage() {
  const { data: certificados, isLoading, isError } = useQuery({
    queryKey: ['certificados'],
    queryFn: certificadoService.getMeusCertificados,
  });

  const baixar = useMutation({
    mutationFn: (certificado: CertificadoEmitido) =>
      certificadoService.baixarPdf(certificado.id, certificado.codigo),
    onError: () => window.alert('Não foi possível baixar o PDF. Tente novamente.'),
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Meus Certificados</h1>
          <p className="text-gray-600 mt-2">
            Certificados emitidos ao concluir cursos e trilhas. Qualquer pessoa pode conferir a autenticidade pelo código.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : isError ? (
          <div className="card text-center py-10 text-red-600">
            Não foi possível carregar seus certificados. Tente novamente em instantes.
          </div>
        ) : certificados && certificados.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificados.map((certificado) => {
              const status = STATUS[certificado.status] ?? STATUS.valido;
              const StatusIcon = status.Icon;

              return (
                <div key={certificado.id} className="card flex flex-col">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="text-yellow-600" size={24} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        {certificado.tipo === 'trilha' ? 'Trilha' : 'Curso'}
                      </p>
                      <p className="font-semibold text-gray-900 leading-tight">{certificado.titulo}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${status.className}`}>
                      <StatusIcon size={14} />
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-500">Carga: {formatarCarga(certificado.cargaHorariaMinutos)}</span>
                  </div>

                  <dl className="text-sm space-y-1 mb-5">
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Emitido em</dt>
                      <dd className="text-gray-900">{formatarData(certificado.emitidoEm)}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Válido até</dt>
                      <dd className="text-gray-900">{formatarData(certificado.validoAte)}</dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-gray-500">Código</dt>
                      <dd className="text-gray-900 font-mono text-xs">{certificado.codigo}</dd>
                    </div>
                  </dl>

                  <div className="mt-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => baixar.mutate(certificado)}
                      disabled={baixar.isPending}
                      className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      <Download size={16} />
                      <span>{baixar.isPending ? 'Gerando...' : 'Baixar PDF'}</span>
                    </button>
                    <a
                      href={`/certificados/validar/${certificado.codigo}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary flex items-center justify-center"
                      title="Abrir validação pública"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Award className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum certificado ainda</h3>
            <p className="text-gray-600">Conclua um curso ou uma trilha para receber seu primeiro certificado.</p>
          </div>
        )}
      </div>
    </div>
  );
}
